import React, { useMemo } from 'react'
import clsx from 'clsx'
import {
  ChatBubbleOvalLeftIcon,
  DocumentTextIcon,
  FaceSmileIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'
import { Text } from '../../ui'
import { SocialPost } from '../../../types/social'
import AuthorAvatar from './AuthorAvatar'
import { DashboardCardSkeleton } from '../DashboardSkeleton'
import { feedCardClass, getRankBadgeClass, getTopActiveUsers } from './socialUtils'
import { formatUserNameWithDepartment } from '../../../utils/userDisplayName'

const TOP_USERS_LIMIT = 5

export interface TopActiveUsersPanelProps {
  posts: SocialPost[]
  loading: boolean
}

const TopActiveUsersPanel: React.FC<TopActiveUsersPanelProps> = ({ posts, loading }) => {
  const topUsers = useMemo(
    () => getTopActiveUsers(posts, TOP_USERS_LIMIT),
    [posts]
  )

  return (
    <aside className={feedCardClass}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <UsersIcon className="h-5 w-5 text-primary-500 flex-shrink-0" />
          <div>
            <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
              Usuários mais ativos
            </Text>
            <Text size="xs" color="muted">
              Mais postagens, comentários e reações
            </Text>
          </div>
        </div>
      </div>

      <div className="p-2">
        {loading && (
          <div className="space-y-2 p-2">
            {Array.from({ length: 3 }, (_, i) => (
              <DashboardCardSkeleton key={i} className="h-14" />
            ))}
          </div>
        )}

        {!loading && topUsers.length === 0 && (
          <div className="px-3 py-6 text-center">
            <UsersIcon className="h-8 w-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <Text size="xs" color="muted">
              Os usuários com mais atividade no feed aparecerão aqui.
            </Text>
          </div>
        )}

        {!loading && topUsers.length > 0 && (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700/80">
            {topUsers.map((user, index) => {
              const rank = index + 1

              return (
                <li
                  key={user.userId}
                  className="flex items-center gap-2.5 px-3 py-3 rounded-lg"
                >
                  <span
                    className={clsx(
                      'flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold',
                      getRankBadgeClass(rank)
                    )}
                  >
                    {rank}
                  </span>

                  <AuthorAvatar
                    name={user.name}
                    avatarPath={user.avatar_url}
                    size="sm"
                  />

                  <span className="min-w-0 flex-1">
                    <Text size="xs" className="font-semibold text-gray-900 dark:text-white truncate">
                      {formatUserNameWithDepartment(user.name, user.department)}
                    </Text>
                    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                      <span className="inline-flex items-center gap-1" title="Postagens">
                        <DocumentTextIcon className="h-3.5 w-3.5" />
                        {user.posts}
                      </span>
                      <span className="inline-flex items-center gap-1" title="Comentários">
                        <ChatBubbleOvalLeftIcon className="h-3.5 w-3.5" />
                        {user.comments}
                      </span>
                      <span className="inline-flex items-center gap-1" title="Reações">
                        <FaceSmileIcon className="h-3.5 w-3.5" />
                        {user.reactions}
                      </span>
                      <span className="font-medium text-primary-600 dark:text-primary-400">
                        {user.total} {user.total === 1 ? 'ação' : 'ações'}
                      </span>
                    </span>
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </aside>
  )
}

export default TopActiveUsersPanel
